export { KB_ALL_EXTRA_ATTRIBUTES, KB_ALL_EXTRA_HTML_ATTRIBUTES, KB_ALL_EXTRA_TAGS, KB_EXTRA_ATTRIBUTES, KB_EXTRA_TAGS, KB_SRC_PROTOCOLS, KB_TAG_ATTRIBUTES, toHtmlAttribute } from './chunk-52BSRVZQ.js';

// src/tokens.ts
var KB_BRANDS = {
  skycell: {
    primary: "#0369a1",
    primaryHover: "#075985",
    primaryDark: "#38bdf8"
  },
  validaide: {
    primary: "#00a69c",
    primaryHover: "#00857d",
    primaryDark: "#33c2b9"
  }
};
var KB_NEUTRALS = {
  bg: "#f4f6f9",
  panel: "#ffffff",
  line: "#d7dde5",
  muted: "#475569",
  fg: "#0f172a"
};
var KB_RADII = { sm: 4, md: 8, lg: 12 };
var KB_FONT_STACK = '"Inter", system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif';

// src/markdown.ts
function slugify(input) {
  return input.toLowerCase().trim().replace(/[^\w\s-]/g, "").replace(/[\s_]+/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "");
}
function transformWikilinks(markdown, basePath = "") {
  return markdown.replace(
    /\[\[([^\]|]+?)(?:\|([^\]]+?))?\]\]/g,
    (_m, target, label) => {
      const slug = slugify(String(target));
      const text = (label ?? target).toString().trim();
      return `[${text}](${basePath}/kb/${slug})`;
    }
  );
}
var KB_LINK_RE = /^\/kb\/([^/?#]+)/;
function matchKbSlug(href) {
  return href.match(KB_LINK_RE)?.[1] ?? null;
}
function stripLeadingH1(markdown) {
  return markdown.replace(/^\s*#\s+[^\n]+\n+/, "");
}
function excerpt(markdown, max = 160) {
  const plain = markdown.replace(/```[\s\S]*?```/g, " ").replace(/[-#>*_`~]/g, " ").replace(/\[\[([^\]|]+?)(?:\|([^\]]+?))?\]\]/g, "$2$1").replace(/\[([^\]]*)\]\([^)]*\)/g, "$1").replace(/\s+/g, " ").trim();
  return plain.length > max ? plain.slice(0, max).trimEnd() + "\u2026" : plain;
}
var WORDS_PER_MINUTE = 200;
function readingMinutes(markdown) {
  const prose = markdown.replace(/```[\s\S]*?```/g, " ").replace(/!\[[^\]]*]\([^)]*\)/g, " ").replace(/\[([^\]]*)]\([^)]*\)/g, "$1").replace(/<[^>]+>/g, " ").replace(/[#>*_`~|-]/g, " ");
  const words = prose.split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / WORDS_PER_MINUTE));
}
var KB_MEDIA_BUCKET = "kb-images";
var MEDIA_PREFIX = `/${KB_MEDIA_BUCKET}/`;
function resolveMediaUrl(path, origin) {
  if (!path.startsWith("/")) return path;
  const base = origin.replace(/\/+$/, "");
  return path.startsWith(MEDIA_PREFIX) ? `${base}/storage/v1/object/public${path}` : `${base}${path}`;
}
var EXTERNAL_LINK_ATTRS = {
  target: "_blank",
  rel: "noopener noreferrer"
};
function isExternalHref(href) {
  return /^https?:\/\//i.test(href);
}
function hardenExternalLink(href) {
  return isExternalHref(href) ? EXTERNAL_LINK_ATTRS : {};
}
var DEFAULT_FOLDER = "General";
function topCategory(folder) {
  return folder.split("/").filter(Boolean)[0] || DEFAULT_FOLDER;
}
function subCategory(folder) {
  return folder.split("/").filter(Boolean)[1] ?? null;
}
function groupByFolder(items, sortItems = (x) => x) {
  const groups = /* @__PURE__ */ new Map();
  for (const item of items) {
    const key = item.folder || DEFAULT_FOLDER;
    const list = groups.get(key);
    if (list) list.push(item);
    else groups.set(key, [item]);
  }
  return [...groups.entries()].map(([folder, list]) => [folder, sortItems(list)]);
}
function groupByCategory(items, sortItems = (x) => x) {
  const paths = items.map((i) => (i.folder || DEFAULT_FOLDER).split("/").map((s) => s.trim()).filter(Boolean));
  const first = paths[0] ?? [];
  let skip = 0;
  while (paths.length > 0 && paths.every((p) => p.length > skip + 1 && p[skip] === first[skip])) {
    skip++;
  }
  const categories = /* @__PURE__ */ new Map();
  items.forEach((item, i) => {
    const segs = (paths[i] ?? []).slice(skip);
    const category = segs[0] ?? DEFAULT_FOLDER;
    const rest = segs.slice(1).join(" / ");
    const section = rest && rest.toLowerCase() !== category.toLowerCase() ? rest : null;
    let sections = categories.get(category);
    if (!sections) categories.set(category, sections = /* @__PURE__ */ new Map());
    const list = sections.get(section);
    if (list) list.push(item);
    else sections.set(section, [item]);
  });
  return [...categories.entries()].map(([category, sections]) => {
    const entries = [...sections.entries()].map(
      ([s, list]) => [s, sortItems(list)]
    );
    entries.sort(([a], [b]) => (a === null ? -1 : 0) - (b === null ? -1 : 0));
    return { category, sections: entries, count: entries.reduce((n, [, l]) => n + l.length, 0) };
  });
}

// src/index.ts
var ARTICLE_LIST_COLUMNS = "id,slug,title,folder,app_id,tags,access_roles,published,updated_at";
var READER_ARTICLE_VIEW = "articles_reader";
var READER_TRANSLATION_VIEW = "article_translations_reader";
function isPublicArticle(accessRoles) {
  return accessRoles.length === 0 || accessRoles.includes("BASIC_ACCESS");
}
function isShareableArticle(article) {
  return isPublicArticle(article.access_roles) && article.no_auth === true;
}
var LANGUAGES = [
  { code: "en", label: "English", nativeLabel: "English" },
  { code: "de", label: "German", nativeLabel: "Deutsch" }
];
var SOURCE_LANGUAGE = "en";
var LANGUAGE_CODES = LANGUAGES.map((l) => l.code);
function isSourceLanguage(code) {
  return code === SOURCE_LANGUAGE;
}
function normalizeLanguage(code) {
  return code && LANGUAGE_CODES.includes(code) ? code : SOURCE_LANGUAGE;
}
function languageLabel(code) {
  return LANGUAGES.find((l) => l.code === code)?.nativeLabel ?? code;
}

export { ARTICLE_LIST_COLUMNS, DEFAULT_FOLDER, EXTERNAL_LINK_ATTRS, KB_BRANDS, KB_FONT_STACK, KB_LINK_RE, KB_MEDIA_BUCKET, KB_NEUTRALS, KB_RADII, LANGUAGES, LANGUAGE_CODES, READER_ARTICLE_VIEW, READER_TRANSLATION_VIEW, SOURCE_LANGUAGE, excerpt, groupByCategory, groupByFolder, hardenExternalLink, isExternalHref, isPublicArticle, isShareableArticle, isSourceLanguage, languageLabel, matchKbSlug, normalizeLanguage, readingMinutes, resolveMediaUrl, slugify, stripLeadingH1, subCategory, topCategory, transformWikilinks };
